import { useEffect } from 'react'

import {
  getGuestMediaFileType,
  isGuestMediaExpired,
} from '~publish/helpers/guestMedia'
import type { GuestMedia } from '~publish/helpers/guestMedia'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'
import type { Image } from '~publish/legacy/composer/composer/entities/factories'

export function useGuestPreviewImages(
  media: readonly GuestMedia[],
  access: GuestMediaAccessController,
): { images: readonly Image[]; unavailableCount: number } {
  useEffect(() => {
    const now = Date.now()
    let nextRefresh = Infinity

    for (const asset of media) {
      if (isGuestMediaExpired(asset)) continue
      const state = access.states.get(asset.assetUrl)
      if (state === undefined) {
        access.requestAccess(asset)
      } else if (state.status === 'ready') {
        if (state.accessUrlExpiresAt <= now) {
          access.requestAccess(asset)
        } else {
          nextRefresh = Math.min(nextRefresh, state.accessUrlExpiresAt - now)
        }
      }
    }

    if (nextRefresh === Infinity) return
    const timeout = window.setTimeout(() => {
      for (const asset of media) {
        const state = access.states.get(asset.assetUrl)
        if (
          state?.status === 'ready' &&
          state.accessUrlExpiresAt <= Date.now()
        ) {
          access.requestAccess(asset)
        }
      }
    }, nextRefresh)
    return (): void => window.clearTimeout(timeout)
  }, [access, media])

  const images: Image[] = []
  let unavailableCount = 0

  for (const asset of media) {
    const state = access.states.get(asset.assetUrl)
    if (
      isGuestMediaExpired(asset) ||
      state?.status === 'expired' ||
      state?.status === 'error'
    ) {
      unavailableCount += 1
    } else if (state?.status === 'ready') {
      images.push({
        url: state.accessUrl,
        width: asset.width ?? null,
        height: asset.height ?? null,
        altText: asset.altText ?? null,
        mediaType: getGuestMediaFileType(asset.mimeType),
        name: asset.fileName,
      })
    }
  }

  return { images, unavailableCount }
}
